"use client";

import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";

interface AggregateData {
  date: string;
  value: string;
  yoy: string;
}

interface HomePriceData {
  label: string;
  value: string;
  aggregateData: AggregateData[];
}

interface PriceTrajectoryGaugeProps {
  homePrices: HomePriceData[];
  yearOverYearSalePrice: string;
}

// Helper to parse price strings into numbers
function parsePriceString(price: string): number {
  return Number(price.replace(/[^0-9.-]/g, ""));
}

// Helper to parse percentage strings into numbers
function parsePercentageString(percentage: string): number {
  return Number(percentage.replace(/[^0-9.-]/g, "")) / 100;
}

function calculateTrend(data: AggregateData[], months: number): number {
  if (data.length < months) return 0;

  const current = parsePriceString(data[0].value);
  const past = parsePriceString(
    data[Math.min(months - 1, data.length - 1)].value,
  );

  return ((current - past) / past) * 100;
}

function getTrendDescription(
  threeMonthTrend: number,
  yearOverYear: number,
): string {
  if (threeMonthTrend < -5) return "strongly cooling";
  if (threeMonthTrend < -2) return "cooling";
  if (threeMonthTrend < 2 && threeMonthTrend > -2) return "stable";
  if (threeMonthTrend < 5) return "heating";
  return "strongly heating";
}

function getTrendColor(trend: number): string {
  if (trend < -5) return "text-blue-500";
  if (trend < -2) return "text-blue-400";
  if (trend < 2 && trend > -2) return "text-gray-500";
  if (trend < 5) return "text-orange-400";
  return "text-orange-500";
}

function getGaugeRotation(trend: number): string {
  // Convert trend to a rotation between -60 (cooling) and 60 (heating) degrees
  const rotation = Math.max(-60, Math.min(60, trend * 6));
  return `rotate(${rotation}deg)`;
}

function getGaugeColor(trend: number): string {
  if (trend < -5) return "from-blue-500 to-blue-600";
  if (trend < -2) return "from-blue-400 to-blue-500";
  if (trend < 2 && trend > -2) return "from-gray-400 to-gray-500";
  if (trend < 5) return "from-orange-400 to-orange-500";
  return "from-orange-500 to-orange-600";
}

export function PriceTrajectoryGauge({
  homePrices,
  yearOverYearSalePrice,
}: PriceTrajectoryGaugeProps) {
  const [isCalculating, setIsCalculating] = useState(true);

  const medianSalePrice = homePrices.find(
    (price) => price.label === "Median Sale Price",
  );
  if (!medianSalePrice) return null;

  const currentPrice = parsePriceString(medianSalePrice.value);
  const threeMonthTrend = calculateTrend(medianSalePrice.aggregateData, 3);
  const yearOverYear = parsePercentageString(yearOverYearSalePrice);

  const trendDescription = getTrendDescription(threeMonthTrend, yearOverYear);
  const trendColor = getTrendColor(threeMonthTrend);

  useEffect(() => {
    const timer = setTimeout(() => setIsCalculating(false), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Card className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white" />
      <div className="relative p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Price Trajectory</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="text-sm text-muted-foreground hover:text-gray-900">
                What is this?
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>
                  Shows the direction and strength of price movements in this
                  market, comparing short-term and long-term trends.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Current Price and YoY Change */}
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <div className="text-3xl font-semibold">
              {formatCurrency(currentPrice)}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Current median price
            </div>
          </div>
          <div className="text-right">
            <div
              className={cn(
                "text-lg font-semibold flex items-center gap-1",
                yearOverYear > 0 ? "text-emerald-600" : "text-red-600",
              )}
            >
              {yearOverYear > 0 ? (
                <TrendingUp className="h-5 w-5" />
              ) : (
                <TrendingDown className="h-5 w-5" />
              )}
              {yearOverYear > 0 ? "+" : ""}
              {(yearOverYear * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-500 mt-1">Year over year</div>
          </div>
        </div>

        {/* Enhanced Gauge Visualization */}
        <div className="relative h-[160px] mb-6 ">
          <div className="absolute inset-x-0 bottom-0 h-[160px] flex items-center justify-center">
            <div className="relative w-[280px] h-[140px]">
              {/* Gauge Labels - More compact positioning */}
              <div className="absolute -top-2 inset-x-0 flex justify-between">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  <div className="text-sm font-medium text-gray-600">
                    Cooling
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-medium text-gray-600">
                    Heating
                  </div>
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                </div>
              </div>

              {/* Market Labels */}
              <div className="absolute -top-8 inset-x-0 flex justify-between">
                <div className="text-xs text-gray-500">
                  Market favors buyers
                </div>
                <div className="text-xs text-gray-500">
                  Market favors sellers
                </div>
              </div>

              <div className="relative w-full h-full overflow-hidden">
                {/* Gauge Base Layer */}
                <div className="absolute inset-0">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-100 via-gray-100 to-orange-100 opacity-50 rounded-t-full" />
                  <div className="absolute inset-0 rounded-t-full border-4 border-gray-100" />
                </div>

                {/* Gauge Ticks */}
                <div className="absolute inset-0">
                  {Array.from({ length: 11 }).map((_, i) => (
                    <div
                      key={`tick-${i}-${Math.random()}`}
                      className="absolute bottom-0 left-1/2 h-[140px] w-[2px] origin-bottom"
                      style={{
                        transform: `rotate(${-60 + i * 12}deg)`,
                      }}
                    >
                      <div
                        className={cn(
                          "h-2 w-[2px]",
                          i === 5
                            ? "bg-gray-300"
                            : i < 5
                              ? "bg-blue-200"
                              : "bg-orange-200",
                        )}
                      />
                    </div>
                  ))}
                </div>

                {/* Gauge Sections */}
                <div className="absolute inset-0">
                  <div className="absolute inset-0 rounded-t-full bg-gradient-to-r from-blue-500/10 via-transparent to-transparent" />
                  <div className="absolute inset-0 rounded-t-full bg-gradient-to-l from-orange-500/10 via-transparent to-transparent" />
                </div>

                {/* Gauge Needle */}
                <div
                  className={cn(
                    "absolute left-1/2 -translate-x-[1px] bottom-0 origin-bottom transition-transform duration-1000",
                    "flex flex-col items-center",
                  )}
                  style={{ transform: getGaugeRotation(threeMonthTrend) }}
                >
                  {/* Needle Stem */}
                  <div className="h-[100px] w-[2px] bg-gradient-to-b from-gray-900 to-gray-700" />

                  {/* Needle Head */}
                  <div className="relative -mt-[2px]">
                    <div className="absolute -left-2 -top-2 w-4 h-4">
                      <div className="absolute inset-0 animate-ping bg-gray-900 rounded-full opacity-20" />
                      <div className="absolute inset-[2px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-full shadow-lg" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trend Description */}
        <div className="text-center">
          <div
            className={cn(
              "text-xl font-semibold mb-2",
              getTrendColor(threeMonthTrend),
            )}
          >
            Market Is {trendDescription}
          </div>
          <div className="text-sm text-gray-600">
            Prices are {threeMonthTrend > 0 ? "up" : "down"}{" "}
            {Math.abs(threeMonthTrend).toFixed(1)}% over the last 3 months
          </div>
        </div>

        {/* Trend Timeline */}
        <div className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-gray-100">
          <div className="text-center">
            <div className="text-sm font-semibold text-gray-600">3 Months</div>
            <div
              className={cn(
                "text-lg font-semibold mt-1",
                threeMonthTrend > 0 ? "text-emerald-600" : "text-red-600",
              )}
            >
              {threeMonthTrend > 0 ? "+" : ""}
              {threeMonthTrend.toFixed(1)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold text-gray-600">6 Months</div>
            <div
              className={cn(
                "text-lg font-semibold mt-1",
                calculateTrend(medianSalePrice.aggregateData, 6) > 0
                  ? "text-emerald-600"
                  : "text-red-600",
              )}
            >
              {calculateTrend(medianSalePrice.aggregateData, 6) > 0 ? "+" : ""}
              {calculateTrend(medianSalePrice.aggregateData, 6).toFixed(1)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold text-gray-600">1 Year</div>
            <div
              className={cn(
                "text-lg font-semibold mt-1",
                yearOverYear > 0 ? "text-emerald-600" : "text-red-600",
              )}
            >
              {yearOverYear > 0 ? "+" : ""}
              {(yearOverYear * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
